import { useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const BeforeAfterSlider = ({
  beforeSrc,
  afterSrc,
  beforeAlt = "Vorher",
  afterAlt = "Nachher",
  beforeLabel = "Vorher",
  afterLabel = "Nachher",
  maxWidthClass = "max-w-2xl",
}) => {
  const containerRef = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.clientX);
  };
  const stop = () => {
    dragging.current = false;
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 4));
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${maxWidthClass} aspect-[3/4] overflow-hidden select-none cursor-ew-resize`}
      style={{ touchAction: "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      data-testid="before-after-slider"
    >
      {/* After (base layer) */}
      <img
        src={afterSrc}
        alt={afterAlt}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Before (clipped to slider position) */}
      <img
        src={beforeSrc}
        alt={beforeAlt}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />

      {/* Labels */}
      <span className="absolute top-4 left-4 bg-black/70 text-white text-xs uppercase tracking-[0.25em] px-4 py-2 pointer-events-none">
        {beforeLabel}
      </span>
      <span className="absolute top-4 right-4 bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] px-4 py-2 pointer-events-none">
        {afterLabel}
      </span>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div
          role="slider"
          aria-label="Vorher-Nachher-Vergleich"
          aria-valuenow={Math.round(pos)}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
          onKeyDown={onKeyDown}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center pointer-events-auto cursor-ew-resize"
          data-testid="before-after-handle"
        >
          <ChevronLeft className="h-4 w-4 text-foreground -mr-1" strokeWidth={2.5} />
          <ChevronRight className="h-4 w-4 text-foreground -ml-1" strokeWidth={2.5} />
        </div>
      </div>
    </div>
  );
};
